import { Injectable } from '@angular/core';
import { BullhornEntityTypes, BullhornNamespace } from 'datasource-service-lib';
import { EntityControllerService } from 'entity-picker-lib';
import { IDataSourceApiService, IEntity, IRecentActivityEntityProfile } from 'interfaces-lib';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { IRecentActivityProfileService } from '../../common-components/recent-activity/irecentactivityprofileservice';
import { ServiceProviderService } from '../service.provider.service';

@Injectable({
  providedIn: 'root',
})
export class BullhornRecentActivityProfileService implements IRecentActivityProfileService {
  private apiService: IDataSourceApiService;

  private entityProfileSelectedSubject = new ReplaySubject<IRecentActivityEntityProfile>();
  recentActivityEntityProfileSelected$ = this.entityProfileSelectedSubject.asObservable();

  constructor(private entityControllerService: EntityControllerService, sps: ServiceProviderService) {
    this.apiService = sps.getDataSourceApiService(BullhornNamespace.name);
    this.entityControllerService.entitySelected$.subscribe((entity) => {
      if (!entity) {
        return;
      }
      this.getRecentActivityProfile(entity).subscribe((profile) => this.entityProfileSelectedSubject.next(profile));
    });
  }

  getRecentActivityProfile(entity: IEntity): Observable<IRecentActivityEntityProfile> {
    return this.apiService.get(entity, { fields: this.getFields(entity) }).pipe(map((fullEntity) => this.transform(fullEntity)));
  }

  private getFields(entity: IEntity): string {
    switch (entity.type) {
      case BullhornEntityTypes.Candidate:
        return 'id,firstName,lastName,companyName';
      case BullhornEntityTypes.ClientCorporation:
        return 'id,name';
      case BullhornEntityTypes.JobOrder:
        return 'id,title,clientCorporation(name)';
      case BullhornEntityTypes.Placement:
        return 'id,candidate(id,firstName,lastName),clientCorporation(name),jobOrder(title)';
      case BullhornEntityTypes.ClientContact:
        return 'id,name,clientCorporation(name)';
      case BullhornEntityTypes.Opportunity:
        return 'id,title,clientCorporation(name)';
      default:
        return 'id';
    }
  }

  private transform(entityData: any): IRecentActivityEntityProfile {
    switch (entityData.type) {
      case BullhornEntityTypes.Candidate:
        return {
          id: entityData.id,
          name: `${entityData.firstName} ${entityData.lastName}`,
          type: entityData.type,
          companyName: entityData.companyName,
        } as IRecentActivityEntityProfile;
      case BullhornEntityTypes.ClientCorporation:
        return {
          id: entityData.id,
          name: entityData.name,
          type: entityData.type,
        } as IRecentActivityEntityProfile;
      case BullhornEntityTypes.JobOrder:
        return {
          id: entityData.id,
          type: entityData.type,
          name: `${entityData.title ? entityData.title : ''}`,
          companyName: entityData.clientCorporation.name,
        } as IRecentActivityEntityProfile;
      case BullhornEntityTypes.Placement:
        return {
          id: entityData.id,
          type: entityData.type,
          name: `${entityData.candidate.firstName} ${entityData.candidate.lastName} - ${entityData.jobOrder.title}`,
          companyName: entityData.clientCorporation.name,
        } as IRecentActivityEntityProfile;
      case BullhornEntityTypes.ClientContact:
        return {
          id: entityData.id,
          name: entityData.name,
          type: entityData.type,
          companyName: entityData.clientCorporation.name,
        } as IRecentActivityEntityProfile;
      case BullhornEntityTypes.Opportunity:
        return {
          id: entityData.id,
          name: entityData.title,
          type: entityData.type,
          companyName: entityData.clientCorporation.name,
        } as IRecentActivityEntityProfile;
      default:
        throw new Error(`${entityData.type} not recognised generating profile.`);
    }
  }
}
